import React, { useEffect, useState } from "react";
import { auth, db } from "./firebase";
import { signOut } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import styles from "./AdminDashboard.module.css";
import CreateClinicEvent from "./CreateClinicEvent";
import ActiveClinicEvents from "./ActiveClinicEvents";
import StudentData from "./StudentData";
import { collection, query, where, onSnapshot, getDocs } from "firebase/firestore";
import logo from "./optolog_logo.png";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer
} from "recharts";


function AdminDashboard() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("home");
  const user = auth.currentUser;
  const [cohort, setCohort] = useState("OD-1");
  const [stats, setStats] = useState({
    totalEncounters: 0,
    totalExposures: 0,
    ratio: 0,
  });

  const handleLogout = async () => {
    await signOut(auth);
    navigate("/login");
  };
  const [monthlyData, setMonthlyData] = useState([]);

const getMonthYear = (timestamp) => {
  const dateObj = timestamp?.toDate ? timestamp.toDate() : new Date(timestamp);
  return dateObj.toLocaleString("default", { month: "short", year: "numeric" }); // e.g., "Jul 2025"
};

const fetchCohortLogStats = async (cohort, setStats) => {
  const logsRef = collection(db, "logs");

  const q =
    cohort === "All Cohorts"
      ? query(logsRef)
      : query(logsRef, where("cohort", "==", cohort));

  const snapshot = await getDocs(q);

  let totalEncounters = 0;
  let totalExposures = 0;

  snapshot.forEach((doc) => {
    const data = doc.data();
    console.log("📄 Log data:", data);

    if (data.involvement && data.involvement.includes("Encounter")) totalEncounters++;
    if (data.involvement && data.involvement.includes("Exposure")) totalExposures++;

  });

  const ratio = totalExposures === 0
    ? totalEncounters
    : (totalEncounters / totalExposures).toFixed(2);

console.log("✅ Calculated Stats:", {
  totalEncounters,
  totalExposures,
  ratio,
});

  setStats({
    totalEncounters,
    totalExposures,
    ratio,
  });
}; // ✅ This closing brace was missing

useEffect(() => {
  const logsRef = collection(db, "logs");
  const q =
    cohort === "All Cohorts"
      ? query(logsRef)
      : query(logsRef, where("cohort", "==", cohort));

  const unsubscribe = onSnapshot(q, (snapshot) => {
    console.log("📡 Snapshot received for stats");

    let totalEncounters = 0;
    let totalExposures = 0;

    snapshot.forEach((doc) => {
      const data = doc.data();
      console.log("📄 Log data:", data);

      if (data.involvement && data.involvement.includes("Encounter")) {
        totalEncounters++;
      }

      if (data.involvement && data.involvement.includes("Exposure")) {
        totalExposures++;
      }
    });

    const ratio =
  totalExposures === 0
    ? totalEncounters
    : (totalEncounters / totalExposures).toFixed(2);

console.log("📊 Final stats being set:", {
  totalEncounters,
  totalExposures,
  ratio,
});

setStats({
  totalEncounters,
  totalExposures,
  ratio,
});
  });

  return () => unsubscribe();
}, [cohort]);





// Fetch monthly encounter chart data
useEffect(() => {
  const logsRef = collection(db, "logs");
const q = cohort === "All Cohorts"
  ? query(logsRef)
  : query(logsRef, where("cohort", "==", cohort));

const unsubscribe = onSnapshot(q, (snapshot) => {

    console.log("📡 Snapshot received for monthly chart");

    const monthlyCount = {};

    snapshot.forEach((doc) => {
      const data = doc.data();
      console.log("📄 Log data:", data);


      if (data.involvement && data.involvement.includes("Encounter") && data.date){
        const month = getMonthYear(data.date);
        if (!monthlyCount[month]) monthlyCount[month] = 0;
        monthlyCount[month]++;
      }
    });

    const sortedMonths = Object.keys(monthlyCount)
  .map((monthStr) => {
    const [monthName, year] = monthStr.split(" ");
    const date = new Date(`${monthName} 1, ${year}`);
    return { monthStr, date };
  })
  .sort((a, b) => a.date - b.date)
  .slice(-12)
  .map(({ monthStr }) => ({
    month: monthStr,
    count: monthlyCount[monthStr],
  }));

setMonthlyData(sortedMonths);

  });

  return () => unsubscribe(); // Cleanup listener on unmount
}, [cohort]);



  return (
  <div className={styles.wrapper}>
    {/* 🔵 Top Bar */}
    <div className={styles.topBar}>
      <img src={logo} alt="OptoLog Logo" className={styles.logo} />

      <div className={styles.schoolName}>
        Rocky Mountain University - College of Optometric Medicine
      </div>

      <div className={styles.rightSection}>
        <span className={styles.userInfo}>{user?.email || "Admin"}</span>
        <button onClick={handleLogout} className={styles.logoutButton}>
          Log Out
        </button>
      </div>
    </div>

    {/* 🔘 Tab Navigation */}
    <div className={styles.tabBar}>
  {[
    { key: "home", label: "Home" },
    { key: "create clinic event", label: "Create Clinic Event" },
    { key: "active clinic events", label: "Active Clinic Events" },
    { key: "students", label: "Student Data" },
  ].map(({ key, label }) => (
    <button
      key={key}
      className={activeTab === key ? styles.activeTab : styles.tab}
      onClick={() => setActiveTab(key)}
    >
      {label}
    </button>
  ))}
</div>


    {/* ✅ HOME TAB CONTENT */}
    {activeTab === "home" && (
      <div style={{ maxWidth: "900px", margin: "0 auto", padding: "2rem" }}>
        {/* 🔽 Cohort Selector */}
        <div className={styles.cohortSelectContainer}>
          <label htmlFor="cohort">Select Cohort:</label>
          <select
            id="cohort"
            value={cohort}
            onChange={(e) => setCohort(e.target.value)}
            className={styles.selectDropdown}
          >
            <option value="OD-1">OD-1</option>
            <option value="OD-2">OD-2</option>
            <option value="OD-3">OD-3</option>
            <option value="OD-4">OD-4</option>
            <option value="All Cohorts">All Cohorts</option>
          </select>
        </div>

        {/* 📊 Summary Stats */}
        <div className={styles.summaryGrid}>
          <div className={styles.summaryBox}>
            <h3>Total Encounters</h3>
            <p>{stats.totalEncounters}</p>
          </div>
          <div className={styles.summaryBox}>
            <h3>Total Exposures</h3>
            <p>{stats.totalExposures}</p>
          </div>
          <div className={styles.summaryBox}>
            <h3>Enc/Exp Ratio</h3>
            <p>{stats.ratio}</p>
          </div>
        </div>

        {/* 📈 Monthly Encounter Trend Chart */}
        <h3 style={{ marginTop: "40px", textAlign: "center", color: "#20466b" }}>
          Monthly Encounter Trend
        </h3>
        <div style={{ width: "100%", maxWidth: "700px", margin: "0 auto" }}>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={monthlyData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Line
                type="monotone"
                dataKey="count"
                stroke="#115680"
                strokeWidth={2}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    )}

    {activeTab === "create clinic event" && (
  <div style={{ maxWidth: "900px", margin: "0 auto", padding: "2rem" }}>
    <CreateClinicEvent />
  </div>
)}

{activeTab === "active clinic events" && (
  <ActiveClinicEvents />
)}

{activeTab === "students" && (
  <div style={{ maxWidth: "900px", margin: "0 auto", padding: "2rem" }}>
    <StudentData />
  </div>
)}

  </div>
);

}

export default AdminDashboard;

